import cn from 'classnames'
import { ChangeEvent, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from "react-router-dom"
import { appInit } from '../../../../config'
import { login } from "../../../../redux/actions"
import { appError, setApplicationToCreate, setAutoLogout } from "../../../../redux/slices/app"
import { Store } from "../../../../redux/store"
import { RequestState, __STORAGE } from "../../../../utils/enums"
import { buildCreateAccountEvent, buildPasswordResetEvent, buildSigninEvent } from '../../../../utils/floodlightEvents'
import { useDangerousHTML, useEmailValidation, useEmbeddedLink } from '../../../../utils/hooks'
import appRoutes from '../../../../utils/routes'
import Utils from "../../../../utils/utils"
import Loader, { LoaderMode } from "../../../Loader"
import { useCaptcha } from '../../../Presentational/Captcha'
import Checkbox from '../../../Presentational/Checkbox'
import Input from '../../../Presentational/Input'
import SVG from '../../../Presentational/SVG'
import BaseView from '../Base'
import Footer from './footer'
import { useSocialLoginHandler } from '../../Onboarding/SocialLoginCallback/useSocialLoginHandler'
import SocialButtonsSection from '../../../../ui-4.0/components/SocialButton/SocialButtonsSection'
import styles from './login.module.scss'

type FormData = {
    email: string
    password: string
    keepSigned: boolean
}

type FormDataKeys = keyof FormData

type FormDataIndexed = FormData & {
    [key: string]: any
}

type Flags = { [K in keyof FormDataIndexed]?: any }

interface LoginProps extends RouteComponentProps {

}

const Login = ({ history }: LoginProps) => {
    const [values, setValues] = useState<FormDataIndexed>(() => ({
        email: Utils.getQueryPrefill('email') || '',
        password: '',
        keepSigned: Utils.getStorageBoolean('keepLogged')
    }))
    const [validation, setValidation] = useState<Flags>({})
    const [inProgress, setInProgress] = useState(false)
    const { socialLoading, handleSocialLogin } = useSocialLoginHandler()
    const { captcha, reset: resetCaptcha, executeCaptcha } = useCaptcha()
    const invalidCode = useRef(false)
    const willUnmount = useRef(false)
    const loginViewedFired = useRef(false)
    const loginRequest = useSelector(
        (state: Store) => state.Api.Login,
        shallowEqual
    )

    const dispatch = useDispatch()

    const { t } = useTranslation(['account', 'common', 'translation'])

    const state = useSelector(
        (state: Store) => state.App,
        shallowEqual
    )
    const { loggedIn, userEmailPreverified, autoLogout, error: requestError, redirectedFromWTR, twoFa, language, userProfile, userIP } = state

    const validateEmail = useEmailValidation(t)

    const validateField = useCallback(async (name: FormDataKeys, value: any) => {
        switch (name) {
            case 'email':
                return validateEmail(value)
            case 'password':
                return value ? '' : t('common:password.errors.notDefined')
            default:
                return ''
        }
    }, [t, validateEmail])

    const validate = useCallback(async () => {
        const email = await validateField('email', values.email)
        const password = await validateField('password', values.password)
        return email === '' && password === ''
    }, [validateField, values.email, values.password])

    const { d } = useDangerousHTML()

    useEffect(() => {
        if (loginViewedFired.current) return
        loginViewedFired.current = true
        window.dataLayer?.push(buildSigninEvent('viewed', {
            referrer: document.referrer,
            loggedIn,
            language,
            country: userProfile?.country?.code2 || userIP?.country_code || '',
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (requestError)
            setInProgress(false)
    }, [requestError])

    // Redirect to Check2FA page when login succeeds but 2FA is required
    useEffect(() => {
        if (loginRequest.state === RequestState.Success && twoFa.enabled && !twoFa.valid) {
            history.push(appRoutes.account.check2fa)
        }
    }, [loginRequest.state, twoFa.enabled, twoFa.valid, history])

    useEffect(() => {
        switch (loginRequest.state) {
            case RequestState.None:
            case RequestState.InProgress:
                break
            case RequestState.Success:
                // setInProgress(false)
                break
            case RequestState.Error:
                setInProgress(false)
                if (loginRequest.error === 'NOT_AUTHORIZED') {
                    let error
                    // const response: APIResponse<LoginResponse> = loginRequest.response
                    // const remainingAttempts = response.payload[0].result.remainingAttempts
                    // if (remainingAttempts === undefined) {
                    error = t('translation:NOT_AUTHORIZED_INLINE')
                    // }
                    // else {
                    //     if (remainingAttempts > 0)
                    //         error = d(`${t('translation:NOT_AUTHORIZED_INLINE')} <strong>${t('translation:RemainingAttempts', { count: remainingAttempts })}</strong>`)
                    //     else
                    //         error = d(`${t('translation:NOT_AUTHORIZED_INLINE')} <strong>${t('translation:accountLocked')}</strong>`)
                    // }
                    setValidation({
                        password: error,
                        email: '  ',
                    })
                } else {
                    dispatch(appError(Utils.statusError(loginRequest.error || '')))
                }
        }
    }, [d, dispatch, loginRequest, t])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const error = params.get('error')
        if (error === 'social_login_cancelled') {
            // Silent on cancel — just strip the param.
            history.replace(appRoutes.account.login)
        } else if (error === 'social_login_failed') {
            dispatch(appError(Utils.customError(t('account:login.socialLoginFailed'))))
            history.replace(appRoutes.account.login)
        } else if (error === 'tfa_expired') {
            dispatch(appError(Utils.customError(t('account:login.tfaExpired'))))
            history.replace(appRoutes.account.login)
        }
    }, [dispatch, t, history])

    const clearValidation = (name: string) => {
        setValidation({ ...validation, [name]: '' })
        if (name === 'code') invalidCode.current = false
    }

    const onChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setValues({ ...values, [name]: value })
        clearValidation(name)
    }

    const onBlur = async (name: string, value: any) => {
        if (!willUnmount.current) {
            const validationResult = await validateField(name as FormDataKeys, value)

            if (!willUnmount.current)
                setValidation({
                    ...validation,
                    [name]: validationResult
                })
        }
    }

    useEffect(() => {
        const checkWTR = async () => {
            await appInit
            if (redirectedFromWTR) {
                Utils.postMessageToWTR({ message: 'restart' })
            }
        }
        checkWTR().catch(console.error)
    }, [redirectedFromWTR])

    const handleLogin = async (e: MouseEvent) => {
        e.preventDefault()
        dispatch(setAutoLogout(false))

        const valid = await validate()
        if (!values.email)
            setValidation({
                ...validation,
                email: t('common:email.errors.notDefined')
            })

        if (!values.password)
            setValidation({
                ...validation,
                password: t('common:password.errors.notDefined')
            })

        if (!values.email && !values.password)
            setValidation({
                email: t('common:email.errors.notDefined'),
                password: t('common:password.errors.notDefined')
            })

        if (!valid) return

        window.dataLayer?.push(buildSigninEvent('submitted', {
            referrer: document.referrer,
            loggedIn,
            language,
            country: userProfile?.country?.code2 || userIP?.country_code || '',
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
            provider: 'portal',
            status: 'clicked',
            code: 'na',
            email: values.email,
        }))

        setInProgress(true)

        const token = await executeCaptcha()

        dispatch(login({
            email: values.email,
            password: values.password,
            keepLogged: values.keepSigned,
            recaptchaResponse: token,
        }))
        resetCaptcha()
    }

    const handleRedirectToDashboard = (e: MouseEvent) => {
        e.preventDefault()
        history.push(appRoutes.dashboard.accounts.root)
    }

    const createAccount = () => {
        window.dataLayer?.push(buildCreateAccountEvent('clicked', {
            referrer: document.referrer,
            loggedIn,
            language,
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
            provider: 'portal',
        }))
        // clear referrer value to indicate that flow is starting from the portal, not wtr
        sessionStorage.removeItem('wtrApp')
        dispatch(setApplicationToCreate(undefined))
        localStorage.removeItem(__STORAGE.creatingIBApplication)
        willUnmount.current = true
        history.push(`${appRoutes.account.createAccount.live}`)
    }

    const resetPassword = () => {
        window.dataLayer?.push(buildPasswordResetEvent('started', {
            referrer: document.referrer,
            loggedIn,
            language,
            country: userProfile?.country?.code2 || userIP?.country_code || '',
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
        }))
        willUnmount.current = true
        history.push(appRoutes.account.resetPasswordStep1)
    }

    /*     const error = useMemo(() => {
            return <div className={styles.maintenance}>{t('maintenance')}</div>
        }, [t])
     */

    const disabled = inProgress || loggedIn || socialLoading
    const linkStyle = cn(styles.small, { [styles.disabled]: disabled })

    const createLink = useEmbeddedLink({
        text: t('login.createLink'),
        onClick: createAccount,
        className: linkStyle
    })




    const form = <>
        <form className={styles.loginForm} autoComplete="off">
            {(inProgress || socialLoading) && <Loader mode={LoaderMode.CoverParent} />}
            {userEmailPreverified && <div className={styles['email-confirmed']}>
                <SVG name='circleCheck' />
                {t('login.verified')}
            </div>
            }
            <h2>{t('login.captionv4')}</h2>

            {autoLogout && <div className={styles.autologout}>
                {t("autoLogoutMessage", { interval: state.inactivityTimeout })}
            </div>}

            <div className={styles.formContent}>
                <Input
                    name="email"
                    type="email"
                    value={values.email}
                    disabled={inProgress}
                    placeholder={t('account:emailPlaceholder')}
                    caption={t('common:email.caption')}
                    validation={validation.email}
                    isFloatedLabel={true}
                    autoComplete="off"
                    onChange={onChangeValue}
                    onBlur={onBlur}
                />

                <div className={styles.password}>
                    <div className={styles.forgottenPassword} onClick={resetPassword}>
                        {t('login.forgottenPassword')}
                    </div>

                    <Input
                        name="password"
                        value={values.password}
                        disabled={inProgress}
                        errorClassName={styles['pass-error']}
                        placeholder={t('account:passwordPlaceholder')}
                        caption={t('common:password.caption')}
                        validation={validation.password}
                        type='password'
                        autoComplete="off"
                        validateSecure={false}
                        onChange={onChangeValue}
                        onBlur={onBlur}
                        showPasswordEye
                    />
                </div>

                <Checkbox
                    name="keepSigned"
                    text={t('login.keepSignedIn')}
                    wrapperClassName={styles.wrapper}
                    value={values.keepSigned}
                    disabled={inProgress}
                    optional
                    onChange={(name, value) => {
                        setValues({ ...values, [name]: value })
                        clearValidation(name)
                    }}
                />
                {captcha}

                <button
                    className={cn("green", styles.loginButton)}
                    type="submit"
                    disabled={disabled}
                    onClick={loggedIn ? handleRedirectToDashboard : handleLogin}
                >
                    {loggedIn ? t('login.alreadyLogged') : t('common:login')}
                </button>
            </div>

            <SocialButtonsSection
                onSocialLogin={handleSocialLogin}
                disabled={disabled}
            />

            <div className={styles.backButton}>
                {createLink}
            </div>
        </form>
    </>

    return (
        <BaseView customFooter={<Footer />}>
            {form}
        </BaseView>
    )
}

export default Login
